import React from 'react';

// Could-buy location-interest glyphs — the canonical Figma "Layouts" icon exports
// (Building / District / Proximity / Street), 24×24, filled. Single `color` prop so
// the same icon reads white on a green selected chip and dark on a light suggestion
// chip. Used by the could-buy form chips (MainStep) + the map picker chips.

interface IconProps {
  color?: string;
}

// Office building — the anchor building chip.
export const BuildingChipIcon = ({ color = '#fff' }: IconProps) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10.25 14H8.25V12H10.25V14Z" fill={color} />
    <path d="M15.75 14H13.75V12H15.75V14Z" fill={color} />
    <path d="M10.25 9H8.25V7H10.25V9Z" fill={color} />
    <path d="M15.75 9H13.75V7H15.75V9Z" fill={color} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17 2C19.2091 2 21 3.79086 21 6V23H3V6C3 3.79086 4.79086 2 7 2H17ZM7 4C5.89543 4 5 4.89543 5 6V21H11V18H13V21H19V6C19 4.89543 18.1046 4 17 4H7Z"
      fill={color}
    />
  </svg>
);

// House + tree — a named area / district chip.
export const DistrictChipIcon = ({ color = '#29363d' }: IconProps) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14.5 17.9997H12.5V15.9997H14.5V17.9997Z" fill={color} />
    <path d="M14.5 12.9997H12.5V10.9997H14.5V12.9997Z" fill={color} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.4307 3.97044C12.5892 2.86727 14.4108 2.86727 15.5693 3.97044L18.3789 6.64623C18.7752 7.02361 18.9999 7.54825 19 8.09544V23.9997H0V13.7283C0.000178478 13.4745 0.0969187 13.2298 0.270508 13.0447L2.81152 10.3347C3.9967 9.07078 6.0033 9.07078 7.18848 10.3347L8 11.1999V8.09544C8.0001 7.61656 8.17197 7.15515 8.48047 6.79466L8.62109 6.64623L11.4307 3.97044ZM5.72949 11.7019C5.33449 11.281 4.6655 11.281 4.27051 11.7019L2 14.1238V21.9997H8V14.1238L5.72949 11.7019ZM14.1895 5.41869C13.8033 5.05115 13.1967 5.05115 12.8105 5.41869L10 8.09544V21.9997H17V8.09544L14.1895 5.41869Z"
      fill={color}
    />
    <path
      d="M21.5 13.9997C22.8806 13.9997 23.9998 17.1188 24 18.4997C24 19.5246 23.3825 20.4038 22.5 20.7898V23.9997H20.5V20.7898C19.6175 20.4038 19 19.5246 19 18.4997C19.0002 17.1188 20.1194 13.9997 21.5 13.9997Z"
      fill={color}
    />
  </svg>
);

// Pin over a radius ellipse — the "within 500 m" proximity chip.
export const PinChipIcon = ({ color = '#29363d' }: IconProps) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20.2402 11.0134C22.6668 12.3201 24 14.1068 24 16.0535C24 18.0001 22.6669 19.7871 20.2402 21.0671C17.9469 22.2938 14.96 22.907 12 22.907C9.04 22.907 6.07977 22.2938 3.75977 21.0671C1.33313 19.7871 1.75308e-05 18.0001 0 16.0535C0 14.1068 1.33321 12.3201 3.75977 11.0134L4.69336 12.7732C2.98669 13.6799 2 14.8804 2 16.0271C2.00019 17.1737 2.98687 18.3735 4.69336 19.28C8.72003 21.4134 15.28 21.4134 19.3066 19.28C21.0131 18.3735 21.9998 17.1737 22 16.0271C22 14.8804 21.0133 13.6799 19.3066 12.7732L20.2402 11.0134Z"
      fill={color}
    />
    <path
      d="M12 5.89331C12.5448 5.89331 12.9871 6.33487 12.9873 6.87964C12.9873 7.42456 12.5449 7.86694 12 7.86694C11.4552 7.86676 11.0137 7.42445 11.0137 6.87964C11.0139 6.33499 11.4553 5.89349 12 5.89331Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0264 1.89331C14.7729 1.89331 17.0135 4.13313 17.0137 6.87964C17.0137 9.33288 13.4933 13.6794 12.7998 14.533L12.0264 15.4666L11.2529 14.533C10.5588 13.6786 7.04004 9.3059 7.04004 6.87964C7.04022 4.13324 9.25331 1.89349 12.0264 1.89331ZM12 3.89331C10.3468 3.89333 9.01385 5.22647 9.01367 6.87964C9.01367 7.97297 10.5867 10.427 12 12.2937C13.4133 10.4004 14.9863 7.94631 14.9863 6.87964C14.9861 5.22646 13.6532 3.89331 12 3.89331Z"
      fill={color}
    />
  </svg>
);

// Thin white "×" for removing a selected chip (the .cbf-chip__remove wrapper applies
// the Figma's semi-transparent opacity).
export const ChipRemoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 4 12 12M12 4 4 12" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Road between two kerbs — a street-level chip (phase-2 street option).
export const StreetChipIcon = ({ color = '#29363d' }: IconProps) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7.14746 0.193359L4.50293 23.998L2.75 23.8037L5.39453 -0.000976562L7.14746 0.193359Z" fill={color} />
    <path d="M21.2539 23.8037L19.502 23.998L16.8574 0.193359L18.6104 -0.00195312L21.2539 23.8037Z" fill={color} />
    <path d="M12.8838 23.9014H11.1201V21.6973H12.8838V23.9014Z" fill={color} />
    <path d="M12.8838 17.8184H11.1201V13.9385H12.8838V17.8184Z" fill={color} />
    <path d="M12.8838 10.0596H11.1201V6.18066H12.8838V10.0596Z" fill={color} />
    <path d="M12.8838 2.30078H11.1201V0.0966797H12.8838V2.30078Z" fill={color} />
  </svg>
);
